import React, { useState, useEffect, useRef } from "react";
import "./Otp-Verify.scss";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FiArrowLeft, FiCheckCircle } from "react-icons/fi";
import { ToastContainer, Zoom } from "react-toastify";
import AuthLayout from "./Auth-Layout";
import { verifyUser } from "../../redux/slices/sliceActions";
import customToasts from "../assets/reusableComponents/myToasts";

const OtpVerify = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const inputRefs = useRef([]);

  const { loading, message, user } = useSelector(
    (state) => state.landlordslice
  );

  const token = user?.data?.accessToken;
  const userPhone = user?.data?.phone || user?.user_phone_number || "";

  // Focus first input on mount
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleDigitChange = (index, value) => {
    // Only accept numeric inputs
    if (value && !/^\d+$/.test(value)) return;

    const newDigits = [...otpDigits];

    // Handle paste of complete code
    if (value.length > 1) {
      const pastedCode = value.slice(0, 6).split("");
      pastedCode.forEach((char, idx) => {
        if (idx < 6) newDigits[idx] = char;
      });
      setOtpDigits(newDigits);
      const nextFocus = Math.min(pastedCode.length, 5);
      inputRefs.current[nextFocus]?.focus();
      return;
    }

    newDigits[index] = value;
    setOtpDigits(newDigits);

    // Auto-advance to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const fullOtpCode = otpDigits.join("");

  const handleSubmit = (e) => {
    e.preventDefault();

    if (fullOtpCode.length < 6) {
      customToasts.error("Please enter the complete 6-digit verification code");
      return;
    }

    customToasts
      .promise(
        dispatch(
          verifyUser({
            otp_code: fullOtpCode,
            token,
          })
        ).unwrap(),
        {
          pending: "Verifying account...",
          success: "Account verified successfully!",
          error: "Verification failed. Please check your code.",
        }
      )
      .catch((err) => {
        console.error("Verification error:", err);
      });
  };

  // Redirect on successful verification
  useEffect(() => {
    if (!loading && message === "verify success") {
      navigate("/signin");
    }
  }, [message, loading, navigate]);

  return (
    <AuthLayout
      title="Verify Account"
      subtitle="Almost there! Enter your OTP."
      subtitleLinkText="Sign in"
      subtitleLinkTo="/signin"
    >
      <form className="otp-verify-form" onSubmit={handleSubmit} noValidate>
        <div className="otp-icon-header">
          <div className="icon-circle">
            <FiCheckCircle className="check-icon" />
          </div>
          <p className="otp-instructions">
            We've sent a 6-digit verification code to{" "}
            {userPhone ? (
              <strong className="phone-highlight">
                {userPhone.slice(0, 4)}...{userPhone.slice(-3)}
              </strong>
            ) : (
              "your registered phone number"
            )}
            . Enter it below to activate your account.
          </p>
        </div>

        {/* 6-Digit Code Inputs */}
        <div className="digits-container">
          {otpDigits.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              maxLength={6}
              className={`digit-box ${digit ? "filled" : ""}`}
              value={digit}
              onChange={(e) => handleDigitChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              autoComplete="one-time-code"
            />
          ))}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className="auth-submit-btn"
          disabled={loading || fullOtpCode.length < 6}
        >
          {loading ? <span className="btn-spinner" /> : "Verify Account"}
        </button>

        <div className="otp-footer">
          <Link to="/signin" className="back-link">
            <FiArrowLeft className="link-icon" />
            <span>Return to login</span>
          </Link>
        </div>
      </form>

      <ToastContainer
        position="bottom-left"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        transition={Zoom}
      />
    </AuthLayout>
  );
};

export default OtpVerify;
